import blogListStyle from "./blogList.module.less";
import {BlogListItem} from "@/screens/Blog/blogListItem";
import ColoredScrollbars from "@/components/scrollBars/ColorScrollBars";
import {Bubbling} from "@/components/button/bubbling";
import {useCallback, useEffect, useState} from "react";
import curPostStore from "@/stores/curPostStore";
import {observer} from "mobx-react-lite";
import {pushHeadingHash, scrollToHeadingAnchor} from "@/screens/Blog/headingAnchor";

export const BlogList = observer(({
    posts,
    hasMore = false,
    clickHandler,
    nextPage,
    isMobile = false,
    isImmersive = false,
    onMobileSidebarOpenChange,
    isDesktopSidebarOpen = false,
    onDesktopSidebarOpenChange,
    isChromeCollapsed = false,
    isCompactDesktop = false
}) => {
    const panelWidth = 'clamp(360px, 34vw, 500px)';
    const collapsedHeadWidth = 'clamp(220px, 18vw, 290px)';
    const contentTop = 'clamp(82px, 10vh, 100px)';
    const collapsedBottomGap = '12px';
    const normalBottomGap = '96px';
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    useEffect(() => {
        if (!isMobile) {
            setMobileSidebarOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobile && isImmersive) {
            setMobileSidebarOpen(false);
        }
    }, [isImmersive, isMobile]);

    useEffect(() => {
        if (onMobileSidebarOpenChange) {
            onMobileSidebarOpenChange(isMobile && mobileSidebarOpen);
        }
    }, [isMobile, mobileSidebarOpen, onMobileSidebarOpenChange]);

    useEffect(() => {
        if (onDesktopSidebarOpenChange) {
            onDesktopSidebarOpenChange(!isMobile && isDesktopSidebarOpen);
        }
    }, [isDesktopSidebarOpen, isMobile, onDesktopSidebarOpenChange]);

    const handleItemClick = useCallback((postId) => {
        clickHandler(postId);
        if (isMobile) {
            setMobileSidebarOpen(false);
        }
    }, [clickHandler, isMobile]);

    const goToHead = useCallback((anchorName) => {
        if (!anchorName) {
            return;
        }
        pushHeadingHash(anchorName);
        scrollToHeadingAnchor(anchorName, {behavior: 'smooth'});
    }, []);

    if (isMobile) {
        return <>
            {mobileSidebarOpen && (
                <div
                    className={blogListStyle.mobileBackdrop}
                    onClick={() => setMobileSidebarOpen(false)}
                />
            )}
            {!isImmersive && (
                <button
                    type="button"
                    className={`${blogListStyle.mobileToggleBtn} ${mobileSidebarOpen ? blogListStyle.mobileToggleBtnOpen : ''} ${isChromeCollapsed ? blogListStyle.mobileToggleBtnCollapsed : ''}`}
                    onClick={() => setMobileSidebarOpen((prev) => !prev)}
                    aria-expanded={mobileSidebarOpen}
                    aria-label={mobileSidebarOpen ? 'close post sidebar' : 'open post sidebar'}
                >
                    <span className={blogListStyle.mobileToggleIcon}>{mobileSidebarOpen ? '✕' : '☰'}</span>
                </button>
            )}
            <aside className={`${blogListStyle.mobileSidebar} ${mobileSidebarOpen ? blogListStyle.mobileSidebarOpen : ''}`}>
                <div className={blogListStyle.mobileSidebarHeader}>
                    <span>POSTS</span>
                </div>
                <ColoredScrollbars style={{
                    position: "relative",
                    width: "100%",
                    height: "calc(100% - var(--mobile-sidebar-header-height, 56px))"
                }}>
                    <div className={`${blogListStyle.blogListBox} ${blogListStyle.mobileListBox}`}>
                        {posts.map((post) => {
                            return <BlogListItem
                                post={post}
                                key={post._id}
                                clickHandler={handleItemClick}
                            />
                        })}
                        {hasMore ? <Bubbling text="MORE" nextPage={nextPage} compact={false}/> : null}
                    </div>
                </ColoredScrollbars>
            </aside>
        </>;
    }

    return <>
        <button
            type="button"
            className={`${blogListStyle.desktopToggleBtn} ${isDesktopSidebarOpen ? blogListStyle.desktopToggleBtnOpen : ''}`}
            onClick={() => onDesktopSidebarOpenChange && onDesktopSidebarOpenChange(!isDesktopSidebarOpen)}
            aria-expanded={isDesktopSidebarOpen}
            aria-label={isDesktopSidebarOpen ? 'close post sidebar' : 'open post sidebar'}
        >
            <span className={blogListStyle.desktopToggleLabel}>POSTS</span>
            <span className={blogListStyle.desktopToggleIcon}>{isDesktopSidebarOpen ? '×' : '☰'}</span>
        </button>
        {!isDesktopSidebarOpen && curPostStore.heads.length >= 1 ? (
            <aside
                className={blogListStyle.desktopCollapsedHeadShell}
                style={{
                    top: `calc(${contentTop} + 54px)`,
                    width: collapsedHeadWidth,
                    bottom: normalBottomGap,
                    height: 'auto',
                }}
            >
                <ColoredScrollbars style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                }}>
                    <div className={`${blogListStyle.blogListBox} ${blogListStyle.collapsedHeadListBox}`}>
                        <div className={blogListStyle.headBox}>
                            {curPostStore.heads.map((id, index) => {
                                return <div
                                    key={id + index}
                                    onClick={() => goToHead(id)}
                                    className={blogListStyle.head + (id === curPostStore.curTitle ? " " + blogListStyle.headHere : "")}
                                >
                                    <span className={blogListStyle.titleSpan}>{id}</span>
                                </div>;
                            })}
                        </div>
                    </div>
                </ColoredScrollbars>
            </aside>
        ) : null}
        <aside
            className={`${blogListStyle.desktopSidebarShell} ${isDesktopSidebarOpen ? blogListStyle.desktopSidebarShellOpen : ''}`}
            style={{
                top: contentTop,
                width: panelWidth,
                bottom: isDesktopSidebarOpen ? collapsedBottomGap : normalBottomGap,
                height: 'auto',
            }}
        >
            <ColoredScrollbars style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
            }}>
                <div className={blogListStyle.blogListBox}>
                    {posts.map((post) => {
                        return <BlogListItem
                            post={post} key={post._id} clickHandler={handleItemClick}
                        />
                    })}
                    {hasMore ? <Bubbling text="MORE" nextPage={nextPage} compact={isCompactDesktop}/> : null}
                </div>
            </ColoredScrollbars>
        </aside>
    </>
});
